<script setup>
/**
 * Batch Add Tasks — CSV → grouped task posts via /api/tasks/add.
 * CSV columns: Task Order, Main Title, Task Title, URL Link, Task Description,
 *              Task Start Date, Task End Date.
 * Integer Task Order = top-level task; decimal = subtask under first task.
 */
import { ref } from "vue";
import { useRouter } from "vue-router";
import { addTaskGroup } from "./api";

const router = useRouter();
const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : `id-${Date.now()}-${Math.random().toString(16).slice(2)}`);

const csvFileName = ref(""), rows = ref([]), loading = ref(false), success = ref(false), error = ref("");

function parseCsv(text) {
  const lines = text.trim().split(/\r?\n/);
  const headers = lines[0].split(",").map(h => h.replace(/^\uFEFF/, "").trim());
  return lines.slice(1).map(line => {
    const v = line.split(",");
    return Object.fromEntries(headers.map((h, i) => [h, (v[i] ?? "").trim()]));
  }).filter(r => r["Task Order"] != null && r["Task Order"] !== "");
}

function onFile(e) {
  const file = e.target.files?.[0]; e.target.value = "";
  if (!file) return;
  const reader = new FileReader();
  reader.onload = ev => { rows.value = parseCsv(ev.target.result).map(r => [r["Task Order"], r["Main Title"], r["Task Title"], r["URL Link"], r["Task Description"], r["Task Start Date"], r["Task End Date"]]); csvFileName.value = file.name; };
  reader.readAsText(file);
}

function buildGroups() {
  const titles = [], groups = [];
  for (const info of rows.value) {
    const isTask = Number(info[0]) % 1 === 0;
    if (!titles.includes(info[1])) {
      titles.push(info[1]);
      if (isTask) groups.push({ title: info[1], startDate: info[5], endDate: info[6], associatedTask: "", tasks: [{ id: uuid(), title: info[2], url: info[3], note: info[4], isCompleted: false, subtasks: [] }], userGuids: [] });
    } else {
      const group = groups.find(g => g.title === info[1]);
      if (!group) continue;
      if (isTask) group.tasks.push({ id: uuid(), title: info[2], url: info[3], note: info[4], isCompleted: false, subtasks: [] });
      else group.tasks[0].subtasks.push({ id: uuid(), title: info[2], url: info[3], note: info[4], isCompleted: false });
    }
  }
  return groups;
}

async function submit() {
  if (!csvFileName.value) { error.value = "Please include a CSV file."; return; }
  loading.value = true; success.value = false; error.value = "";
  try { await Promise.all(buildGroups().map(g => addTaskGroup(g))); success.value = true; }
  catch { error.value = "Error occurred. Please try again."; }
  finally { loading.value = false; }
}
</script>

<template>
  <section>
    <VBtn variant="text" prepend-icon="mdi:arrow-left" class="mb-4" @click="router.push({ name: 'tasks' })">Back</VBtn>
    <h1 class="text-h5 mb-4">Batch Add Tasks</h1>
    <VCard class="pa-4" max-width="560">
      <VAlert v-if="error" type="error" class="mb-4">{{ error }}</VAlert>
      <VAlert v-if="success" type="success" class="mb-4">Batch add successful.</VAlert>
      <p class="text-body-2 text-medium-emphasis mb-4">
        CSV columns: <code>Task Order, Main Title, Task Title, URL Link, Task Description, Task Start Date, Task End Date</code>.<br>
        Integer order = task, decimal = subtask under first task of the group.
      </p>
      <div class="mb-4">
        <div class="text-body-2 font-weight-semibold mb-1">CSV File</div>
        <input type="file" accept=".csv" @change="onFile" />
        <p v-if="csvFileName" class="text-caption text-medium-emphasis mt-1">{{ csvFileName }} — {{ rows.length }} row(s)</p>
      </div>
      <VBtn :loading="loading" :disabled="!csvFileName" @click="submit">Submit</VBtn>
    </VCard>
  </section>
</template>
